import { Grid, makeStyles } from '@material-ui/core'
import DazzedHeadingForGrid from 'components/common/text/DazzedHeadingForGrid'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCanViewCopilotPatients } from '../../hooks/useCopilotAccountOwnerId'
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import ChartLegendIcon from '../../icons/LegendIcon'
import BarGraphWidgetSkeleton from '../../components/common/BarGraphWidget/BarGraphWidgetSkeleton'
import BaseWidget from '../../components/common/BaseWidget'
import { PatientsListTypes } from 'consts/copilotControlConsts'
import { trackEvent } from 'utils/analyticsUtils'
import IconAndText from 'components/common/IconAndText'
import RightArrowIcon from 'components/CopilotControl/icons/RightArrowIcon'

const useStyles = makeStyles(theme => ({
  baseWidget: {
    height: '96%',
    paddingBottom: 0
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  chartContainer: {
    height: '80%',
    width: '90%',
    maxHeight: 300,
    maxWidth: 380
  },
  legend: {
    height: '10%',
    width: '100%',
    maxWidth: 420,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '0 10px'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '10px',
    fontSize: 12
  },
  hoverFooter: {
    height: 50,
    position: 'absolute',
    padding: '0 13px',
    background: 'white',
    width: '100%',
    borderRadius: 24,
    boxShadow: '0px -10px 10px 5px white',
    right: 0,
    bottom: 0,
    display: 'flex',
    zIndex: 3,
    transition: 'opacity 0.5s',
    opacity: 1,
    pointerEvents: 'auto',
    cursor: 'pointer'
  },
  hoverFooterHidden: {
    opacity: 0,
    height: 50,
    pointerEvents: 'none'
  }
}))

const SCANS_BAR_COLOR = '#8FB4FF'
const NEEDS_ATTENTION_BAR_COLOR = '#3C52EF'

const ScansAttentionRatioGraph = ({
  isLoading = false,
  isSelected,
  totalScans = 100,
  totalNeedsAttention = 20,
  onChangePatientsListType
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [hovered, setHovered] = useState(false)
  // A Sales-only doctor override shows totals without patient lists, so there is nothing to open.
  const canViewPatients = useCanViewCopilotPatients()

  const hasAnyData = totalScans > 0 || totalNeedsAttention > 0

  const barsData = [
    {
      name: t('pages.copilotControl.widgets.scansNeedAttentionRatio.legend.taggedScans'),
      value: totalScans,
      legendColor: SCANS_BAR_COLOR
    },
    {
      name: t('pages.copilotControl.widgets.scansNeedAttentionRatio.legend.attentionRequired'),
      value: totalNeedsAttention,
      legendColor: NEEDS_ATTENTION_BAR_COLOR
    }
  ]

  const handleDisplayNeedsAttentionPatientsClicked = useCallback(() => {
    trackEvent('Copilot control - Needs attention see patients clicked')
    onChangePatientsListType(PatientsListTypes.NeedsAttention.type)
  }, [onChangePatientsListType])

  return isLoading ? (
    <BarGraphWidgetSkeleton />
  ) : (
    <div
      style={{ position: 'relative', height: '100%', width: '100%', overflow: 'hidden' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <BaseWidget className={classes.baseWidget} isSelected={isSelected} carousel={false}>
        <div className={classes.root}>
          <Grid container className={classes.legend}>
            {barsData.map((item, index) => (
              <div key={index} className={classes.legendItem}>
                <ChartLegendIcon color={item.legendColor} />
                <DazzedHeadingForGrid regular xs={12} sm={12} md={12} lg={14} xl={14} style={{ marginLeft: 5 }}>
                  {item.name}
                </DazzedHeadingForGrid>
              </div>
            ))}
          </Grid>
          <div className={classes.chartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barsData} margin={{ top: 20, right: 30, left: 0, bottom: 10 }} barCategoryGap="30%">
                <CartesianGrid vertical={false} strokeOpacity={0.5} />
                <XAxis dataKey="name" style={{ opacity: 0.3 }} tick={false} axisLine={false} />
                <YAxis style={{ opacity: 0.4 }} tickCount={5} />

                <Bar dataKey="value" opacity={0.8} radius={[10, 10, 0, 0]}>
                  <Cell key={`scans`} fill={SCANS_BAR_COLOR} />
                  <Cell key={`needsAttention`} fill={NEEDS_ATTENTION_BAR_COLOR} />
                  <LabelList
                    dataKey="value"
                    position="top"
                    style={{ fill: '#000', fontSize: 12, fontWeight: 'bold' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </BaseWidget>
      {hasAnyData && canViewPatients && (
        <div
          className={
            hovered && !isLoading ? classes.hoverFooter : `${classes.hoverFooter} ${classes.hoverFooterHidden}`
          }
        >
          <IconAndText
            reverse
            icon={<RightArrowIcon />}
            text={
              <DazzedHeadingForGrid bold xs={12} sm={12} md={12} lg={14} xl={14} style={{ color: '#3C52EF' }}>
                {t('pages.copilotControl.widgets.engagementWidget.seePatients')}
              </DazzedHeadingForGrid>
            }
            onClick={handleDisplayNeedsAttentionPatientsClicked}
          />
        </div>
      )}
    </div>
  )
}

export default ScansAttentionRatioGraph
